import { Injectable, OnDestroy, inject, effect } from '@angular/core';
import { BehaviorSubject, map, tap } from 'rxjs';
import { Theme } from '../account/account.model';
import { SettingsService } from '../account/settings/settings.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private settingsService = inject(SettingsService);

  private get localTheme() {
    return localStorage['theme'] as Theme | undefined;
  }

  private set localTheme(theme: Theme | undefined) {
    localStorage['theme'] = theme;
  }

  private readonly defaultTheme: Theme = 'light';
  private updateSubject = new BehaviorSubject<Theme>(this.localTheme || this.defaultTheme);

  readonly theme$ = this.updateSubject.pipe(
    tap(theme => this.localTheme = theme),
    map(theme => theme ?? this.defaultTheme)
  );

  constructor() {
    effect(() => {
      const theme = this.settingsService.settings()?.['theme'];
      if (theme && this.updateSubject.value !== theme) {
        this.updateSubject.next(theme);
      }
    });
  }

  get theme() {
    return this.updateSubject.value;
  }

  setTheme(theme: Theme) {
    if (this.updateSubject.value !== theme) {
      this.updateSubject.next(theme);
      this.localTheme = theme;
      this.settingsService.saveSettings({ theme }).subscribe();
    }
  }

  ngOnDestroy() {
    this.updateSubject.complete();
  }
}
